/**
 * What the percentage on a topic row means, and what its colour means.
 *
 * Reported: neither Practice surface said what the number was. A bare "58%"
 * beside a red bar could be read as progress through the topic, share of the
 * bank answered, or a grade — and a student who reads it as "58% complete"
 * draws the opposite conclusion from the one intended.
 *
 * And then QA: the same topic read 58% on one list and 64% on the next,
 * because one list shows recent accuracy and the other all-time accuracy.
 * So the legend names WHICH accuracy it is (METRIC_LABELS), and the caller
 * has to say — there is no default to silently get wrong.
 *
 * SHARED so the two surfaces cannot drift. The bands here are the same four
 * used by accuracyTone/toneFor, which a test already asserts are identical —
 * one legend has to mean one thing, on every page that shows it.
 */
import { METRIC_LABELS } from '@/lib/progress/metric-labels'

export const ACCURACY_BANDS: { label: string; cls: string }[] = [
  { label: 'under 65', cls: 'bg-red-500' },
  { label: '65–74', cls: 'bg-orange-500' },
  { label: '75–84', cls: 'bg-yellow-500' },
  { label: '85+', cls: 'bg-emerald-600' },
]

export default function AccuracyLegend({
  metric,
  trailing,
}: {
  /** Which accuracy the rows above show. */
  metric: 'allTime' | 'recent'
  trailing?: React.ReactNode
}) {
  const label = METRIC_LABELS[metric]
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[10.5px] text-muted-foreground">
      <span title={label.explain}>
        <span className="font-semibold text-foreground">%</span> = {label.inline} ({label.hint})
      </span>
      <span aria-hidden="true" className="hidden h-3 w-px bg-border sm:block" />
      {ACCURACY_BANDS.map((b) => (
        <span key={b.label} className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className={`size-2 rounded-sm ${b.cls}`} />
          {b.label}
        </span>
      ))}
      {trailing && <span className="ml-auto">{trailing}</span>}
    </div>
  )
}
